import { supabase } from '../lib/supabase';

export const userPreferencesService = {
  // Get user preferences
  async get() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_preferences')?.select('*')?.eq('user_id', user?.id)?.single();

    // If no preferences exist, create default ones
    if (error && error?.code === 'PGRST116') {
      return await this.create();
    }

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      theme: data?.theme,
      defaultBucketId: data?.default_bucket_id,
      autoUpload: data?.auto_upload,
      fileValidationRules: data?.file_validation_rules,
      notificationSettings: data?.notification_settings,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Create user preferences
  async create(preferences = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_preferences')?.insert({
        user_id: user?.id,
        theme: preferences?.theme || 'light',
        default_bucket_id: preferences?.defaultBucketId || null,
        auto_upload: preferences?.autoUpload || false,
        file_validation_rules: preferences?.fileValidationRules || {
          maxSize: 104857600,
          allowedTypes: []
        },
        notification_settings: preferences?.notificationSettings || {
          emailNotifications: true,
          uploadComplete: true
        }
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      theme: data?.theme,
      defaultBucketId: data?.default_bucket_id,
      autoUpload: data?.auto_upload,
      fileValidationRules: data?.file_validation_rules,
      notificationSettings: data?.notification_settings,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Update user preferences
  async update(updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const updateData = {};
    if (updates?.theme) updateData.theme = updates?.theme;
    if (updates?.defaultBucketId !== undefined) updateData.default_bucket_id = updates?.defaultBucketId;
    if (updates?.autoUpload !== undefined) updateData.auto_upload = updates?.autoUpload;
    if (updates?.fileValidationRules) updateData.file_validation_rules = updates?.fileValidationRules;
    if (updates?.notificationSettings) updateData.notification_settings = updates?.notificationSettings;

    const { data, error } = await supabase?.from('user_preferences')?.update(updateData)?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      theme: data?.theme,
      defaultBucketId: data?.default_bucket_id,
      autoUpload: data?.auto_upload,
      fileValidationRules: data?.file_validation_rules,
      notificationSettings: data?.notification_settings,
      updatedAt: data?.updated_at
    };
  },

  // Update theme preference
  async updateTheme(theme) {
    return await this.update({ theme });
  },

  // Update default bucket
  async updateDefaultBucket(bucketId) {
    return await this.update({ defaultBucketId: bucketId });
  },

  // Update auto upload setting
  async updateAutoUpload(enabled) {
    return await this.update({ autoUpload: enabled });
  },

  // Update file validation rules
  async updateFileValidationRules(rules) {
    return await this.update({ fileValidationRules: rules });
  },

  // Update notification settings
  async updateNotificationSettings(settings) {
    return await this.update({ notificationSettings: settings });
  },

  // NEW METHOD: Update active folder preference
  async updateActiveFolder(folderId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_preferences')?.update({
        active_folder_id: folderId
      })?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;

    console.log('✅ Active folder updated in user_preferences:', {
      userId: user?.id,
      activeFolderId: folderId
    });

    return {
      id: data?.id,
      userId: data?.user_id,
      theme: data?.theme,
      defaultBucketId: data?.default_bucket_id,
      autoUpload: data?.auto_upload,
      activeFolderId: data?.active_folder_id,
      fileValidationRules: data?.file_validation_rules,
      notificationSettings: data?.notification_settings,
      updatedAt: data?.updated_at
    };
  }
};